import { Action, createActionGroup, createFeature, createFeatureSelector, createReducer, createSelector, emptyProps, on, props } from '@ngrx/store';
import { AllSameKeyValuesEnforcer } from '../helpers/helpers';
import { OnsiteSchedule, OnsiteActivity, BaseOnsiteActivity, NewOnsiteData } from 'globals/store/onsite.data';
import { DateSchedule } from 'globals/store/programs.data';
import { Dictionary } from '@ngrx/entity';
import { userActions } from './user.era';
import { eventsActions } from './events.era';

export const CHANGE_ONSITE_DATE_STRING = "[Onsite] Change onsite date string"
export const CHANGE_ONSITE_DATE_INDEX = "[Onsite] Change onsite date index"
export const LOAD_ONSITE_ACTIVITIES_AND_SCHEDULES = "[Onsite] Load activities and schedules"
export const LOAD_COMPLETE_NO_RESULTS = "[Onsite] Load was empty"
export const CACHE_ONSITE_SCHEDULES = "[Onsite] Cache onsite schedules"
export const CHANGE_TIMELINE_MODE = "[Onsite] Change timeline mode"
export const REMOVE_ACTIVITIES = "[Onsite] Remove Activities"
export const REMOVE_SCHEDULES = "[Onsite] Remove Schedules"

export interface OnsiteState {
	selectedDateString: string
	activities: Dictionary<OnsiteActivity>
	schedules: Dictionary<OnsiteSchedule>
	onsiteSchedule: DateSchedule
  timelineMode: string,
  myTimelineMode: string,
	activitiesLoaded: boolean
}


const scheduleProperties: AllSameKeyValuesEnforcer<OnsiteSchedule> = { sessions: 'sessions', extension: 'extension', day: 'day', date: 'date', id: 'id', title: 'title', type: 'type', scheduleId: 'scheduleId', order: 'order', venueId: 'venueId', location: 'location', timeZoneId: 'timeZoneId' }
export const schedulePropertyNames = new Set(Object.values(scheduleProperties))

const activityProperties: AllSameKeyValuesEnforcer<BaseOnsiteActivity> = { 
  performerIds: 'performerIds', completed: 'completed', createdBy: 'createdBy', 
  extension: 'extension', day: 'day', date: 'date', files: 'files', id: 'id', resourceIds: 'resourceIds',
  labels: 'labels', linkId: 'linkId', tags: 'tags', groupTags:'groupTags', time: 'time', 
  title: 'title', type: 'type', description: 'description', justTime: 'justTime', top: 'top', 
  position: 'position', exhibitorIds: 'exhibitorIds', sponsorIds: 'sponsorIds', assetIds: 'assetIds', attendeeIds: 'attendeeIds' }

export const activityPropertyNames = new Set(Object.values(activityProperties)) // <== meant to be used to enforce only appropriate properties being sent to firebase, to be used with helpers.copyObjectWithProps


const makeInitialState = (): OnsiteState => ({
	selectedDateString: null,
	activities: {},
	schedules: {},
	onsiteSchedule: null,
  timelineMode: 'multiple',
  myTimelineMode: 'single',
	activitiesLoaded: false
})

export const onsiteActions = createActionGroup({
	source: 'Onsite',
	events: {
		'Change onsite date string': props<{ dateString: string }>(),
		'Change onsite date index': props<{ dateIndex: number }>(),
		'Load activities and schedules': props<{ onsiteData: NewOnsiteData }>(),
		'Load complete but empty': emptyProps(),
		'Cache onsite schedules': props<{ schedule: DateSchedule }>(),
		'Change timeline mode': props<{ mode: string, myTimeline: boolean }>(),
		'Remove activities': props<{ activityIds: string[] }>(),
		'Remove schedules': props<{ scheduleIds: string[] }>(),
	}
})

export const onsiteReducers = createReducer(
	makeInitialState(),
	on(userActions.logout, (state) => makeInitialState()),
	on(eventsActions.unloadEvent, (state) => makeInitialState()),
	on(onsiteActions.changeTimelineMode, (state, { mode, myTimeline }) => ({ ...state, timelineMode: myTimeline ? state.timelineMode : mode, myTimelineMode: myTimeline ? mode : state.myTimelineMode })),
	on(onsiteActions.removeActivities, (state, { activityIds }) => {
		const activities = { ...state.activities }
		activityIds.forEach(id => delete activities[id])
		return { ...state, activities }
	}),
	on(onsiteActions.changeOnsiteDateString, (state, { dateString }) => ({ ...state, selectedDateString: dateString })),
	on(onsiteActions.changeOnsiteDateIndex, (state, { dateIndex }) => ({ ...state, selectedDateIndex: dateIndex })),
	on(onsiteActions.loadActivitiesAndSchedules, (state, { onsiteData }) => {
		const { activities, schedules } = onsiteData
		console.log({ onsiteData })
		const newActivities = { ...state.activities }
		if (activities) {
			Object.values(activities).forEach(a => {
				if (!a.time) a.time = '00:00'
				newActivities[a.id] = a
			})
		}
		const newSchedules = { ...state.schedules }
		if (schedules) {
			Object.values(schedules).forEach(s => newSchedules[s.id] = s)
		}
		return {
			...state,
			schedules: newSchedules,
			activities: newActivities,
			activitiesLoaded: true
		}
	}),
	on(onsiteActions.loadCompleteButEmpty, (state) => ({ ...state, activitiesLoaded: true })),
	on(onsiteActions.cacheOnsiteSchedules, (state, { schedule }) => ({ ...state, onsiteSchedule: schedule })),
	on(onsiteActions.removeSchedules, (state, { scheduleIds }) => {
			const schedules = { ...state.schedules }
			scheduleIds.forEach(id => delete schedules[id])
			return { ...state, schedules }
		}),
)

export const onsiteFeatures = createFeature({
	name: 'onsite',
	reducer: onsiteReducers
})

export const onsiteStateSelector = createFeatureSelector<OnsiteState>(onsiteFeatures.name)
